import BaseSync, { ISyncAddSet, ISyncOptions, RunResult, MIN_VERSION } from "./base";
import * as logger from "../utils/logger";
import { handleApi } from "../utils/error";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import Message, { IMessage, IRoute } from "../model/Message";
import Notifications, { NEW_EVENT_NOTIFICATION_STRING, NEW_MESSAGE_NOTIFICATION_STRING } from "../utils/notifications";
import * as common from "./common";
import { getCurrentVehicle } from "../utils/device";
import { getServerDate } from "../utils/storage/date";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { extend, hashToArray } from "../utils/vanilla";
import constants from "../constants";
import { TMultiCallParam } from "../utils/api.interfaces";
import { IModifiedType } from "./sync.types";
import { MobileActions } from "../store/mobile/actions";
import { NODEVICEID, NOUSERID } from "../model/DutyStatusLog";
import { BaseCache, makeUserCache } from "../utils/storage/cache";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { getMessagesDictionaryFromStore, isSystemMessage } from "../ui/messaging/helpers";
import { nativeSpeak, speakSilence } from "../utils/native/nativeSpeak";
import { getNotificationController } from "../utils/native/notificationController";
import { AppType, getAppType } from "../utils/native/nativeHelpers";
import { MessageActions } from "../store/messages/actions";
import { FeedTypeKey } from "./common";
import { RouteActions } from "../store/routes/actions";
import { isDuplicateException, isOverLimitException, isRelationViolatedException } from "../utils/errorHandler/error.types";
import { isInvalidTextMessageUser, isNotInSystemException } from "../utils/errorHandler/error.messages";
import Messages from "../model/Messages";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";

const GETFEED = "GetFeed";

const enum FeedTypes {
    Recipient = "recipient",
    Device = "device",
    User = "user"
}
const MESSAGES_SYNC_RETRY = 60 * 1000;

export const MESSAGE_LIMIT = 500;
export const EVENTS_DISPLAY_LIMIT = 50;

export default class MessagesSync extends BaseSync {
    private readonly userCache: BaseCache<IUser>;
    private deviceFeedVersion: string;
    private recipientFeedVersion: string;
    private userFeedVersion: string;
    private reachedMessageLimit = false;
    private recipientMessageCount = 0;
    private userMessageCount = 0;
    private deviceMessageCount = 0;

    constructor (options: ISyncOptions, private readonly dutyStatusLogService: DutyStatusLogService) {
        super(options);
        this.userCache = makeUserCache(this.store);
    }

    defaults() {
        return {
            name: "Sync: Messages"
        };
    }

    getLogs(): IIdDictionary<IMessage> {
        let apiUser = this.api.getUser();
        return getMessagesDictionaryFromStore(this.store, apiUser?.id || "");
    }

    setLogs(logsToSave: IIdDictionary<IMessage>): void {
        let apiUser = this.api.getUser();
        let updatedLocalLogs = this.removeExpiredLogs(logsToSave);
        apiUser && this.store.dispatch(MessageActions.setMessages({ userId: apiUser.id, messages: updatedLocalLogs }));
    }

    clearDeviceMessages() {
        let messagesDict: IIdDictionary<IMessage> = {};
        let apiUser = this.api.getUser();
        let messages = this.getLogs();
        let currentVehicle = getCurrentVehicle();
        let prunedMessages = hashToArray(messages).filter(message =>
            (message.device && currentVehicle && message.device.id !== NODEVICEID && message.device.id === currentVehicle.id) ||
            (message.recipient && message.recipient.id !== NOUSERID) ||
            (message.user && message.user.id !== NOUSERID && message.user.id === apiUser?.id));

        prunedMessages.forEach(message => {
            messagesDict[message.id] = message;
        });
        this.setLogs(messagesDict);
    }

    removeExpiredLogs(logsToSave: IIdDictionary<IMessage>): IIdDictionary<IMessage> {
        let fromDate = getServerDate();
        let message: IMessage;
        fromDate.setDate(fromDate.getDate() - constants.MESSAGING.MESSAGE_EXPIRY_DAYS);
        Object.keys(logsToSave).forEach(id => {
            message = logsToSave[id];
            if (message.delivered && message.delivered < fromDate.toISOString()) {
                delete logsToSave[message.id];
            }
        });
        return logsToSave;
    }

    checkIfPropertiesNotEqual(local: IMessage, server: IMessage) {
        // we ignore version and client specific properties when comparing if message changed to avoid merge conflicts.
        function replacer(key, value) {
            switch (key) {
                case "version":
                case "_syncCounter":
                    return undefined;
                default:
                    return value;
            }
        }
        return JSON.stringify(local, replacer) !== JSON.stringify(server, replacer);
    }

    updateUnreadTopbarNotifications(localLogs: IIdDictionary<IMessage>, currentUser: IUser | null) {
        let unreadRoutes: { [routeId: string]: boolean } = {};
        let unreadMessageIds: string[] = [];
        let eventLogs: IMessage[] = [];

        for (let logId of Object.keys(localLogs)) {
            let logMessage = localLogs[logId];

            if (logMessage.messageContent?.routeId) {
                // If this routeId has already been processed for an unread incoming message, skip it
                if (unreadRoutes.hasOwnProperty(logMessage.messageContent.routeId)) {
                    continue;
                }
                // Mark this routeId as seen
                unreadRoutes[logMessage.messageContent.routeId] = true;
            }

            if (isSystemMessage(logMessage)) {
                eventLogs.push(logMessage);
            } else if (!logMessage.read && this.isIncomingMessage(logMessage)) {
                unreadMessageIds.push(logMessage.id);
            }
        }

        let hasUnreadMessages = (unreadMessageIds || []).length > 0;
        let unreadTrimmedEventsCount = eventLogs
                .sort(Messages.messageComparator(false))
                .slice(0, EVENTS_DISPLAY_LIMIT)
                .filter((event: IMessage) => !event.read && this.isIncomingMessage(event)).length;

        if (currentUser) {
            this.translator.translate(NEW_MESSAGE_NOTIFICATION_STRING)
                .then(translatedMessage => Notifications.updateUnreadMessagesNotification(unreadMessageIds.length, currentUser.id, translatedMessage))
                .catch(error => handleApi(error));

            this.translator.translate(NEW_EVENT_NOTIFICATION_STRING)
                .then(translatedMessage => Notifications.updateUnreadEventsNotification(unreadTrimmedEventsCount, currentUser.id, translatedMessage))
                .catch(error => handleApi(error));
        }
        this.store.dispatch(MobileActions.setNewMessage(hasUnreadMessages));
    }

    isIncomingMessage = (logMessage: IMessage) => {
        let currentUser = this.api.getUser();
        let currentVehicle = getCurrentVehicle();
        let isCurrentUserSender = logMessage.user && logMessage.user.id !== NOUSERID && logMessage.user.id === currentUser?.id;
        let isCurrentDeviceMessage = logMessage.isDirectionToVehicle && logMessage.device && logMessage.device.id !== NODEVICEID && logMessage.device.id === currentVehicle?.id;
        return (isCurrentDeviceMessage && logMessage.messageContent?.contentType !== "GoTalk") ||
            (!logMessage.isDirectionToVehicle && !isCurrentUserSender && (typeof (logMessage.device) === "undefined" || logMessage.device.id === NODEVICEID));
    };

    mergeTextMessages(serverLogs: IMessage[], existingLogs?: IIdDictionary<IMessage>): {
        mergedLogs: IIdDictionary<IMessage>;
        newServerMessages: IMessage[];
      }  {
        let currentDate = getServerDate();
        let localLogs = existingLogs || this.getLogs() || {}; // existing logs is a parameter used for unit tests
        let newServerMessages: IMessage[] = [];

        // Merge in server logs with local logs
        serverLogs.forEach((serverLog: IMessage) => {
            let server = serverLog;
            let localLog = localLogs[serverLog.id];

            // Server log exists locally
            if (localLog) {
                let local = localLog;
                let previousState = local.previousState;
                let modifiedState = local.modified as IModifiedType;

                /*
                Four scenarios:
                local changed, server same => take local
                local changed, server changed => conflict if local was "edit", merge local and server if local was "add"
                local same, server changed => take server
                local same, server same => do nothing
                */

                // Local changed
                if (modifiedState && previousState) {
                    // Server changed
                    if (this.checkIfPropertiesNotEqual(previousState, server)) {
                        if (modifiedState === "Add") {
                            // Server has a different version, merge them
                            logger.log(logger.Source.SYNC, "Merged local Message with server", this.currentUser);
                            localLogs[serverLog.id] = common.merge(localLogs[serverLog.id], server, { modified: "Set" });
                        } else {
                            logger.error(logger.Source.SYNC, `SYNC: Message conflict (both server and client changed). Local ID: ${ local.id }, Server ID: ${ server.id }`);
                        }
                    }
                } else {
                    // Local same
                    if (this.checkIfPropertiesNotEqual(local, server)) {
                        logger.log(logger.Source.SYNC, "Updated local Message from server", this.currentUser);

                        if (serverLog.activeTo && new Date(serverLog.activeTo) < currentDate) {
                            delete localLogs[serverLog.id];
                        } else {
                            localLogs[serverLog.id] = serverLog;
                        }
                    }
                }
            } else if (!(serverLog.activeTo && new Date(serverLog.activeTo) < currentDate)) {
                if (!serverLog.read && this.isIncomingMessage(serverLog)) {
                    newServerMessages.push(serverLog);
                }
                localLogs[serverLog.id] = serverLog;
            }
        });

        return { mergedLogs: localLogs, newServerMessages };
    }

    notifyUser = (mergedLogs: IIdDictionary<IMessage>, newServerMessages: IMessage[]) => {
        if (mergedLogs) {
            this.updateUnreadTopbarNotifications(mergedLogs, this.currentUser);
        }
        if (newServerMessages.length > 0) {
            this.alertNewMessages(this.store.getState().data.driving, newServerMessages);
        }
    };

    /**
     * When new messages are received and the "tablet" is driving,
     * use the speech synth to audibly alert the driver to messages.
     */
    alertNewMessages = function (isDriving: boolean | undefined, messages: IMessage[]) {
        let notifyMessage: string;
        let topMessage: IMessage;
        let isRoute = false;

        if (isDriving) {
            messages.forEach(message => {
                if (isRoute) {
                    return;
                }

                let intro = "New message";
                let body: string | undefined;

                if (message.read || !message.isDirectionToVehicle || message._audibleAlert) {
                    return;
                }

                switch (message.messageContent?.contentType) {
                    case "Normal":
                        body = message.messageContent.message;
                        break;
                    case "Location":
                        if (message.messageContent.routeId) {
                            isRoute = true;
                            body = `New route: #PAUSE#${ message.messageContent.message }`;
                        } else {
                            body = `New location: #PAUSE#${ message.messageContent.address }`;
                        }
                        break;
                    case "CannedResponse":
                        body = `${ message.messageContent.message }.#PAUSE# Needs reply: ${ message.messageContent.cannedResponseOptions &&
                        message.messageContent.cannedResponseOptions.map(el => el.text).join(", ") }`;
                        break;
                    default:
                        return;
                }

                if (message.messageContent.urgent) {
                    intro = `Urgent ${ intro }`;
                }

                nativeSpeak(intro);
                speakSilence(1000);

                body?.split("#PAUSE#").forEach(function (part: string) {
                    nativeSpeak(part);
                    speakSilence(1000);
                });

                message._audibleAlert = true;
            });
        } else {
            //Checking to see if it is a route message
            topMessage = messages[0];
            if (topMessage.messageContent?.contentType === "Location" && topMessage.messageContent.routeId) {
                notifyMessage = "New message";
            } else {
                notifyMessage = messages.length > 1 ? (`${ messages.length } new messages`) : "New message";
            }
            getNotificationController().nativeNotify(notifyMessage, constants.APP_NAME)
                .catch((error) => {
                    logger.error(logger.Source.NATIVE, `nativeNotify: Failed to notify new messages: "${ error?.message || error }" (AppType: ${ AppType[getAppType()] })`);
                });
        }
    };

    syncLogs(localLogs: IIdDictionary<IMessage>): Promise<void> {
        return new Promise((resolve, reject) => {
            let currentUser = this.api.getUser();
            let currentVehicle = getCurrentVehicle();
            let calls: [IModifiedType, ISyncAddSet<IMessage>][] = [];
            let guids: (string | null)[] = [];   // lookup of guids to calls
            let updateLogStorage = () => {
                this.setLogs(localLogs);
                dispatcher.trigger(DispatcherEvents.SyncMessages);
                resolve();
            };

            // Prepare local logs for push to server
            Object.keys(localLogs).forEach(logKey => {
                let log = localLogs[logKey];

                if (!log.id) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                let isCurrentUserSender = log.user && log.user.id !== NOUSERID && log.user.id === currentUser?.id;
                let isIncomingCurrentDeviceMsg = log.isDirectionToVehicle && log.device && log.device.id !== NODEVICEID && log.device.id === currentVehicle?.id;

                // Only mark incoming local text messages from a different user and incoming messages where admin and driver are same as delivered
                let canMessageMarkAsDelivered = !isCurrentUserSender || (isCurrentUserSender && isIncomingCurrentDeviceMsg);

                if (!log.delivered && log.modified !== "Add" && canMessageMarkAsDelivered) {
                    log.delivered = getServerDate().toISOString();
                    log.modified = "Set";
                }

                if (log.modified) {
                    let sanitizedLog = (log ? extend({}, log) : {}) as IMessage;
                    this.removeClientSideProperties(sanitizedLog);

                    if (log.modified === "Add") {
                        delete (sanitizedLog as any).id;
                    }

                    if (log.modified === "Add" || log.modified === "Set") {
                        if (log._syncCounter === undefined) {
                            log._syncCounter = 5;
                        }

                        if (log._syncCounter > 0) {
                            log._syncCounter--;
                            // Adding guids if adding a new message. Later replaced with multiCall returned bigint id
                            guids.push(log.modified === "Add" ? log.id : null);

                            calls.push([log.modified, {
                                typeName: "TextMessage",
                                entity: sanitizedLog
                            }]);
                        } else {
                            delete localLogs[log.id];
                        }
                    }
                }
            });

            if (calls.length > 0) {
                logger.log(logger.Source.SYNC, common.formatCallForLogger("Messages", calls), currentUser);
                this.api.multiCall(calls, (results) => {
                    let getLocalEntity = (entity: IMessage, idFromServer: string | undefined, index: number) => {
                        // If a guid exists for this call, change localLogs[guid] to the bigint localLogs[result]
                        let guid = guids[index];
                        if (guid && idFromServer) {
                            localLogs[idFromServer] = localLogs[guid];
                            localLogs[idFromServer].id = idFromServer;
                            delete localLogs[guid];
                            return localLogs[idFromServer];
                        }
                        return localLogs[entity.id];
                    };

                    this.resolveSyncedEntities(calls, results, getLocalEntity);

                    // Update localStorage
                    updateLogStorage();
                }, (errorObject) => {
                    if (isOverLimitException(errorObject)) {
                        reject(errorObject);
                        return;
                    }
                    let requestIndex = errorObject.data.requestIndex;
                    let call = calls[requestIndex];
                    let log = call && call[1].entity;

                    if (log && log.modified === "Add" && !log.isDirectionToVehicle) {
                        delete log.delivered;
                    }

                    let logId = log?.id || guids[requestIndex];
                    if (logId && localLogs.hasOwnProperty(logId)) {
                        if (isDuplicateException(errorObject)) {
                            // This log is already in the database, make it a "Set" and sync again
                            localLogs[logId].modified = "Set";
                            this.setLogs(localLogs);
                            this.syncLogs(localLogs).then(resolve, reject);
                            return;
                        }

                        if (isRelationViolatedException(errorObject)) {
                            if (isNotInSystemException(errorObject)) {
                                delete localLogs[logId];
                                this.setLogs(localLogs);
                                this.syncLogs(localLogs).then(resolve, reject);
                                return;
                            }
                            // MYG-129326: the server now sanitizes the user-FK violation; fk_textmessages_users no longer
                            // appears in the error message, so we detect it via the sanitized text rather than the FK switch below.
                            if (isInvalidTextMessageUser(errorObject)) {
                                delete localLogs[logId].user;
                                this.setLogs(localLogs);
                                this.syncLogs(localLogs).then(resolve, reject);
                                return;
                            }

                            let fkConstraint = common.getForeignKeyConstraint(errorObject);
                            if (fkConstraint) {
                                fkConstraint = fkConstraint.toLowerCase();
                                switch (fkConstraint) {
                                    case "fk_textmessages_vehicle": {
                                        let removedVehicle = log.device && log.device.id !== "NoDeviceId" ? log.device : null;
                                        if (removedVehicle) {
                                            if (currentVehicle && currentVehicle.id === removedVehicle.id) {
                                                dispatcher.trigger(DispatcherEvents.VehicleRemoved);
                                            }

                                            Object.keys(localLogs).forEach(messageId => {
                                                let localLog = localLogs[messageId];
                                                if (localLog.device && localLog.device.id !== "NoDeviceId" && localLog.device.id === removedVehicle?.id) {
                                                    delete localLogs[localLog.id];
                                                }
                                            });
                                        } else {
                                            delete localLogs[logId];
                                        }

                                        this.setLogs(localLogs);
                                        this.syncLogs(localLogs).then(resolve, reject);
                                        break;
                                    }
                                    case "fk_textmessages_users_read":
                                        delete localLogs[logId].markedReadBy;
                                        this.setLogs(localLogs);
                                        this.syncLogs(localLogs).then(resolve, reject);
                                        break;
                                    default:
                                        dispatcher.trigger(DispatcherEvents.SyncMessages);
                                        reject(errorObject);
                                }
                                return;
                            }
                        }
                    }

                    dispatcher.trigger(DispatcherEvents.SyncMessages);
                    reject(errorObject);
                });
            } else {
                updateLogStorage();
            }
        });
    }

    getNonCurrentVehicleIdsFromLogs(messageLogsDict: IIdDictionary<IMessage>, currentVehicle: IIdEntity) {
        let visitedVehicleIds = {};
        let vehicleIds: string[] = [];

        for (let key in messageLogsDict) {
            let log = messageLogsDict[key];
            let logVehicleId = log.device?.id;
            if (logVehicleId && logVehicleId !== NODEVICEID && !(logVehicleId in visitedVehicleIds) && logVehicleId !== currentVehicle.id && log.modified) {
                visitedVehicleIds[logVehicleId] = true;
                vehicleIds.push(logVehicleId);
            }
        }
        return vehicleIds;
    }

    fetchData(): Promise<IMessage[]> {
        return new Promise((resolve, reject) => {
            let currentUser = this.api.getUser();
            let currentVehicle = getCurrentVehicle();

            if (!(currentUser && currentUser.id)) {
                this.syncLogs({}).then(() => resolve, reject);
                resolve([]);
                return;
            }

            // Get msgs from up to 1 week ago
            let fromDate = getServerDate();
            fromDate.setDate(fromDate.getDate() - constants.MESSAGING.MESSAGE_EXPIRY_DAYS);

            let calls: TMultiCallParam[] = [];
            let recipientFeedVersion = common.getFeedVersion(this.api, this.store, FeedTypeKey.RECIPIENT_TEXT_MESSAGE);
            let deviceFeedVersion = common.getFeedVersion(this.api, this.store, FeedTypeKey.DEVICE_TEXT_MESSAGE);
            let userFeedVersion = common.getFeedVersion(this.api, this.store, FeedTypeKey.USER_TEXT_MESSAGE);

            calls.push(
                [GETFEED, {
                    typeName: "TextMessage",
                    resultsLimit: MESSAGE_LIMIT,
                    search: {
                        recipientSearch: { id: currentUser.id },
                        fromDate: recipientFeedVersion && recipientFeedVersion !== MIN_VERSION ? undefined : fromDate.toISOString()
                    },
                    fromVersion: recipientFeedVersion || MIN_VERSION
                }]
            );

            calls.push(
                [GETFEED, {
                    typeName: "TextMessage",
                    resultsLimit: MESSAGE_LIMIT,
                    search: {
                        userSearch: { id: currentUser.id },
                        fromDate: userFeedVersion && userFeedVersion !== MIN_VERSION ? undefined : fromDate.toISOString()
                    },
                    fromVersion: userFeedVersion || MIN_VERSION
                }]
            );

            if (currentVehicle && currentVehicle?.id !== NODEVICEID) {
                calls.push(
                    [GETFEED, {
                        typeName: "TextMessage",
                        resultsLimit: MESSAGE_LIMIT,
                        search: {
                            deviceSearch: { id: currentVehicle?.id },
                            fromDate: deviceFeedVersion && deviceFeedVersion !== MIN_VERSION ? undefined : fromDate.toISOString()
                        },
                        fromVersion: deviceFeedVersion || MIN_VERSION
                    }]
                );
            }

            this.api.multiCall(calls, (results: IMessage[][]) => {
                let recipientMessages = results[0] || [];
                recipientMessages = this.populateFeedVersion(recipientMessages, FeedTypes.Recipient, fromDate.toISOString());
                let userMsgs = results[1] || [];
                userMsgs = this.populateFeedVersion(userMsgs, FeedTypes.User, fromDate.toISOString());
                let allMessages = recipientMessages.concat(userMsgs);
                if (results.length > 2) {
                    let deviceMessages = results[2] || [];
                    deviceMessages = this.populateFeedVersion(deviceMessages, FeedTypes.Device, fromDate.toISOString());
                    this.deviceMessageCount = deviceMessages && deviceMessages.length;

                    // filter out system messages that are older than most recent Login date.
                    let loginLogs = this.dutyStatusLogService.getHOSLogs(false, currentUser?.id, false).filter(log => log.status === "Login");
                    let sortedLogs = this.dutyStatusLogService.getSortedHosLogs(loginLogs, true);
                    let loginDateTime = sortedLogs.length ? sortedLogs[0]?.dateTime : undefined;

                    let filteredOutOldMessages = deviceMessages.filter((msg) => {
                        let msgReadDate = msg.read;
                        return !(isSystemMessage(msg) && (loginDateTime && msgReadDate && loginDateTime > msgReadDate));
                    });

                    allMessages = [...allMessages, ...filteredOutOldMessages];
                }
                this.recipientMessageCount = recipientMessages && recipientMessages.length;
                this.userMessageCount = userMsgs && userMsgs.length;
                resolve(this.convertMessagesFromServer(allMessages));
            }, reject);
        });
    }


    populateFeedVersion(results, feedType: FeedTypes, fromDate: string) {
        let messages = (results.data || []) as IMessage[];

        let messagesFromAWeekAgo = messages.filter((msg: IMessage) => msg.sent !== undefined && msg.sent >= fromDate);

        this.setCurrentFeedVersion(feedType, results.toVersion);

        let currentEntity = "";
        if (feedType === FeedTypes.Device) {
            currentEntity = "device";
        } else {
            currentEntity = "user";
        }

        if (feedType === FeedTypes.User) {
            logger.log(logger.Source.SYNC, `Downloaded ${ messages.length } Sent TextMessage(s) for current ` + currentEntity);
        } else {
            logger.log(logger.Source.SYNC, `Downloaded ${ messages.length } TextMessage(s) for current ` + currentEntity);
        }
        return messagesFromAWeekAgo;
    }

    setCurrentFeedVersion(feedType: FeedTypes, version: string) {
        if (feedType === FeedTypes.Recipient) {
            this.recipientFeedVersion = version;
        } else if (feedType === FeedTypes.Device) {
            this.deviceFeedVersion = version;
        } else {
            this.userFeedVersion = version;
        }
    }

    handleData(results: IMessage[]): Promise<IMessage[]> {
        return new Promise((resolve) => {
            let currentUser = this.api.getUser();
            let currentUserId = currentUser ? currentUser.id : undefined;
            let currentVehicle = getCurrentVehicle();

            let allowedContentTypes = ["Normal", "CannedResponse", "Location"];
            let filterNonCurrentVehicleMessages = (message: IMessage) => message.device?.id !== currentVehicle?.id && message.device?.id !== NODEVICEID;
            let filterNonSystem = (message: IMessage) => (message.device?.id === currentVehicle?.id || message.device?.id === NODEVICEID) && message.messageContent
                && message.messageContent.contentType && allowedContentTypes.indexOf(message.messageContent.contentType) > -1;
            let oldMessages = results.filter(filterNonCurrentVehicleMessages);
            let newMessages = results.filter(filterNonSystem);

            logger.log(logger.Source.SYNC, `Downloaded ${ newMessages.length } TextMessage`, currentUser);

            if (newMessages && newMessages.length) {
                let routeIds: string[] = [];
                let locId: string;
                newMessages = newMessages.map(message => {
                    let content = message.messageContent;

                    if (content?.contentType === "Location" && content.routeId && content.address === "Reset stops") {
                        delete content.routeId;
                    }

                    if (content?.routeId) {
                        locId = content.routeId;
                        if (routeIds.indexOf(locId) < 0) {
                            routeIds.push(locId);
                        }
                    }
                    return message;
                });

                if (currentUserId && this.securityService.can("getRouteMessages", currentUserId)) {
                    void this.storeRoutes(routeIds).then(() => resolve(newMessages));
                } else {
                    resolve(newMessages);
                }
                return;
            }
            resolve([...newMessages, ...oldMessages]);
        });
    }

    setFeedVersion(key: common.FeedTypeKey, version: string) {
        common.setFeedVersion(this.api, this.store, key, version);
    }

    storeRoutes(routeIds?: string[]): Promise {
        // Find and store route messages locally
        return new Promise((resolve, reject) => {
            if (!routeIds || !routeIds.length) {
                resolve();
                return;
            }

            // Get routes that our messages use and store them
            let calls: TMultiCallParam[] = routeIds.map(id => ["Get", {typeName: "Route", search: { id: id }}]);
            this.api.multiCall(calls, (routes: IRoute[][]) => {
                let apiUser = this.api.getUser();
                let apiUserId = apiUser ? apiUser.id : "";
                if (!apiUserId) {
                    return;
                }

                // Start with a copy of existing routes that are NOT among the ones we just queried.
                // This ensures routes for other user IDs or routes not part of this specific query are preserved.
                let routeDict: DeepReadonlyObject<IIdDictionary<IRoute>> = this.store.getState().routes[apiUserId] || {};
                let tempRoutes: IIdDictionary<IRoute> = {};

                // First, copy over all routes that were NOT part of the current API query
                Object.keys(routeDict).forEach(id => {
                    if (routeIds.indexOf(id) === -1) {
                        tempRoutes[id] = routeDict[id] as IRoute;
                    }
                });

                // Now, add or update the routes returned by the API call
                routes.forEach(route => {
                    if (route && route.length > 0) {
                        tempRoutes[route[0].id] = route[0];
                    }
                });

                this.store.dispatch(RouteActions.setRoutes({ userId: apiUserId, routes: tempRoutes }));
                resolve();
            }, reject);
        });
    }

    populateSenderNames(mergedLogs: IIdDictionary<IMessage>) {
        return new Promise(resolve => {
            let senderDict = {};
            let apiUserId = this.api.getUser()?.id;
            if (!apiUserId) {
                resolve(mergedLogs);
                return;
            }

            let currentVehicle = getCurrentVehicle();
            let oldMergedLogs = {};
            for (let key in mergedLogs) {
                let log = mergedLogs[key];
                if (log.device?.id !== currentVehicle?.id) {
                    oldMergedLogs[key] = log;
                    delete mergedLogs[key];
                }
            }

            Object.keys(mergedLogs).forEach(id => {
                let message = mergedLogs[id];
                if (message.user && message.user.id !== NOUSERID && !senderDict.hasOwnProperty(message.user.id)) {
                    senderDict[message.user.id] = message.user.id;
                }
            });

            this.userCache.getOrFetch(this.api, hashToArray<IIdEntity>(senderDict).map(senderId => ({ id: senderId })), apiUserId)
                .then(() => resolve({...mergedLogs, ...oldMergedLogs}))
                .catch(error => {
                    // Failed, retry next time
                    handleApi(error);
                    resolve({...mergedLogs, ...oldMergedLogs});
                });
        });
    }

    synchronize(progressCb: common.TSyncCallback, profilingBatcher?: PerformanceProfileBatcher): Promise {
        return this.createProfiledSync(MeasureName.SyncMessages, () => {
            let postponeSync = (eventText: string) => {
                logger.event(logger.Event.SYNCMESSAGESCONFLICT, eventText);
                // If last sync failed due to GetFeed rate limiting (60 per min), schedule messages sync after a minute.
                if (this.runState.lastRunResult === RunResult.OverLimitException || this.reachedMessageLimit) {
                    setTimeout(() => {
                        void this.synchronize(progressCb);
                    }, MESSAGES_SYNC_RETRY);
                }
                progressCb("messages");
            };

            // Last sync might have failed due to an error. Don't repeat those calls
            if (this.didSyncFailRecently()) {
                postponeSync("Skip Messages sync because previous run failed very recently.");
                return Promise.resolve();
            }
            // Last sync reached the limit of downloaded messages. Don't repeat those calls
            if (this.reachedMessageLimit && (getServerDate().getTime() - this.runState.lastRunTimestamp.getTime()) < 30 * 1000) {
                postponeSync("Skip Messages sync because message limit has reached the maximum download capacity.");
                return Promise.resolve();
            }

            return new Promise(resolve => {
                let handleSyncSuccess = () => {
                    this.updateRunStateAtSyncComplete();

                    this.reachedMessageLimit = this.recipientMessageCount >= MESSAGE_LIMIT || this.userMessageCount >= MESSAGE_LIMIT || this.deviceMessageCount >= MESSAGE_LIMIT;
                    this.recipientMessageCount = 0;
                    this.userMessageCount = 0;
                    this.deviceMessageCount = 0;
                };
                let handleSyncError = error => this.updateRunStateAtSyncComplete(error);

                let mergedLogsForNotification: IIdDictionary<IMessage>;
                let newServerMessagesForNotification: IMessage[];
                this.fetchData()
                    .then(serverLogs => this.handleData(serverLogs))
                    .then(serverLogs => {
                        let { mergedLogs, newServerMessages } = this.mergeTextMessages(serverLogs);
                        mergedLogsForNotification = {...mergedLogs};
                        newServerMessagesForNotification = [...newServerMessages];
                        return this.populateSenderNames(mergedLogs);
                    })
                    .then(logs => this.syncLogs(logs))
                    .then(() => {
                        this.notifyUser(mergedLogsForNotification, newServerMessagesForNotification);
                        this.setFeedVersion(FeedTypeKey.RECIPIENT_TEXT_MESSAGE, this.recipientFeedVersion);
                        this.setFeedVersion(FeedTypeKey.DEVICE_TEXT_MESSAGE, this.deviceFeedVersion);
                        this.setFeedVersion(FeedTypeKey.USER_TEXT_MESSAGE, this.userFeedVersion);
                        progressCb("messages");
                        handleSyncSuccess();
                        this.clearDeviceMessages();
                        resolve();
                    })
                    .catch(err => {
                        progressCb("messages", err);
                        resolve();
                        handleSyncError(err);
                    });
            });
        }, profilingBatcher);
    }

    /**
     * Will throw an error causing caller to reject promise
     * @param obj Message object
     */
    checkValue(obj: IMessage) {
        if (!obj.device) {
            throw new Error("Device is not populated");
        }

        if (obj.device && obj.recipient) {
            throw new Error("Cannot have both device and recipient populated");
        }

        if (!obj.isDirectionToVehicle) {
            throw new Error("isDirectionToVehicle is required");
        }

        if (!obj.messageContent) {
            throw new Error("messageContent is required");
        }

        if (!obj.messageContent.contentType) {
            throw new Error("contentType is required in messageContent");
        }
    }

    convertMessagesFromServer(messages: any[]): IMessage[] {
        let newMessages: IMessage[] = [];
        let msg: any;
        if (messages) {
            for (msg of messages) {
                let device: any = msg.device;
                let user: any = msg.user;
                let recipient: any = msg.recipient;
                let markedReadBy: any = msg.markedReadBy;

                if (device === NODEVICEID) {
                    msg.device = { id: NODEVICEID };
                }

                if (user === NOUSERID) {
                    msg.user = { id: NOUSERID };
                }

                if (recipient === NOUSERID) {
                    msg.recipient = { id: NOUSERID };
                }

                if (markedReadBy === NOUSERID) {
                    msg.markedReadBy = { id: NOUSERID };
                }
                newMessages.push(new Message(msg));
            }
        }
        return newMessages;
    }
}